import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Все сделки — 3 дня. Прибыль = сумма * ставка / 100
const CalculatorSection = () => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [customAmount, setCustomAmount] = useState('');
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const plans = [
    { amount: 3000,  rate: 24, labelKey: 'calc_plan1' },
    { amount: 5000,  rate: 29, labelKey: 'calc_plan2' },
    { amount: 10000, rate: 37, labelKey: 'calc_plan3' },
  ];

  const plan = plans[selectedPlan];
  const investAmount = customAmount ? parseFloat(customAmount) || plan.amount : plan.amount;
  const profit = Math.round(investAmount * (plan.rate / 100));
  const total  = investAmount + profit;

  const handlePlanChange = (idx: number) => {
    setAnimating(true);
    setSelectedPlan(idx);
    setCustomAmount('');
    setTimeout(() => setAnimating(false), 300);
  };

  return (
    <section id="calculator" className="py-24 bg-[#111111]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#d4af37] text-xs tracking-widest uppercase font-medium">{t('calc_badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t('calc_title1')}<br />
            <span className="text-[#d4af37]">{t('calc_title2')}</span>
          </h2>
          <p className="text-white/55 text-base max-w-xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t('calc_subtitle')}
          </p>
        </div>

        <div className={`max-w-4xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-[#1a1a1a] border-2 border-[#d4af37]/30 rounded-3xl p-8 md:p-12">

            {/* Планы */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {plans.map((p, i) => (
                <button
                  key={i}
                  onClick={() => handlePlanChange(i)}
                  className={`relative rounded-2xl p-4 md:p-5 text-center cursor-pointer transition-all duration-300 border-2 ${
                    selectedPlan === i
                      ? 'bg-[#d4af37]/15 border-[#d4af37] shadow-lg shadow-[#d4af37]/10'
                      : 'bg-white/3 border-white/10 hover:border-white/20'
                  }`}
                >
                  {i === 1 && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#d4af37] text-black text-xs font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                      {t('calc_popular')}
                    </div>
                  )}
                  <div className={`text-xs tracking-widest uppercase mb-2 font-medium ${selectedPlan === i ? 'text-[#d4af37]' : 'text-white/40'}`}>
                    {t(p.labelKey)}
                  </div>
                  <div className={`text-2xl md:text-3xl font-black mb-1 ${selectedPlan === i ? 'text-white' : 'text-white/60'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>
                    €{p.amount.toLocaleString()}
                  </div>
                  <div className={`text-lg font-bold ${selectedPlan === i ? 'text-[#d4af37]' : 'text-white/40'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {p.rate}{t('calc_per_year')}
                  </div>
                </button>
              ))}
            </div>

            {/* Своя сумма + период (фиксированный) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="text-white/60 text-xs tracking-widest uppercase mb-2 block font-medium">
                  {t('calc_custom_label')}
                </label>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={`${t('calc_custom_placeholder')}${plan.amount.toLocaleString()}`}
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                  min={plan.amount}
                />
              </div>
              <div className="flex flex-col justify-center">
                <div className="bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-xl px-5 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/20 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-[#d4af37] text-xl"></i>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs tracking-widest uppercase mb-0.5">{t('calc_period_label')}</div>
                    <div className="text-[#d4af37] font-black text-xl" style={{ fontFamily: "'Oswald', sans-serif" }}>3</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Результат */}
            <div className={`bg-gradient-to-r from-[#d4af37]/10 to-transparent border border-[#d4af37]/20 rounded-2xl p-6 mb-8 transition-all duration-300 ${animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
              <div className="flex flex-col gap-4">
                <div>
                  <div className="text-white/50 text-sm mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {t('calc_profit_label')}
                  </div>
                  <div className="text-4xl sm:text-5xl md:text-6xl font-black text-[#d4af37]" style={{ fontFamily: "'Oswald', sans-serif", textShadow: '0 0 30px rgba(212,175,55,0.4)' }}>
                    +€{profit.toLocaleString()}
                  </div>
                  <div className="text-white/40 text-sm mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {t('calc_total_label')}<span className="text-white/70">€{total.toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex flex-row gap-3">
                  <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex-1">
                    <div className="text-white/40 text-xs mb-0.5">{t('calc_invested')}</div>
                    <div className="text-white font-bold text-lg" style={{ fontFamily: "'Oswald', sans-serif" }}>€{investAmount.toLocaleString()}</div>
                  </div>
                  <div className="bg-[#d4af37]/10 border border-[#d4af37]/20 rounded-xl px-4 py-3 flex-1">
                    <div className="text-[#d4af37]/60 text-xs mb-0.5">{t('calc_rate')}</div>
                    <div className="text-[#d4af37] font-bold text-lg" style={{ fontFamily: "'Oswald', sans-serif" }}>{plan.rate}{t('calc_per_yr')}</div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => document.getElementById('contacts')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full bg-[#d4af37] text-black font-bold py-5 rounded-2xl hover:bg-[#e8c84a] transition-all duration-200 cursor-pointer text-base tracking-widest uppercase whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('calc_cta')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
            {[
              { icon: 'ri-shield-check-line', titleKey: 'calc_feat1_title', descKey: 'calc_feat1_desc' },
              { icon: 'ri-bar-chart-2-line',  titleKey: 'calc_feat2_title', descKey: 'calc_feat2_desc' },
              { icon: 'ri-file-text-line',    titleKey: 'calc_feat3_title', descKey: 'calc_feat3_desc' },
            ].map((item, i) => (
              <div key={i} className="bg-white/3 border border-white/8 rounded-2xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 rounded-xl flex-shrink-0">
                  <i className={`${item.icon} text-[#d4af37] text-xl`}></i>
                </div>
                <div>
                  <div className="text-white font-semibold text-sm mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>{t(item.titleKey)}</div>
                  <div className="text-white/45 text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{t(item.descKey)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorSection;